import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { updateOrganizationIntegrationSettings } from "../../../../api-client/organization/OrganizationIntegrationSettingsAPIClient";
import {
  OrganizationConfigStatusField,
  OrganizationIntegrationSettings,
  PartnershipType,
  SyncFrequencyPlan,
} from "../../../../models/Entities";
import find from "lodash/find";
import { snakeCaseToSpacedCapitalized } from "../../../../services";
import PartnershipCredentialModal from "./settings/partnership-modal/PartnershipCredentialModal";
import IntegrationSettingsRowOverview from "./IntegrationSettingsRowOverview";
import IntegrationSyncFrequencySection from "./IntegrationSyncFrequencySection";
import useAppContext from "../../../context/useAppContext";
import {
  Accordion,
  Alert,
  ButtonVariant,
  Dialog,
  TextField,
  TextFieldVariant,
  Text,
  Button,
  CopyTextField,
  Link,
} from "@merge-api/merge-javascript-shared";
import { getSanitizedHTML } from "../../../../models/Helpers";

interface Props {
  organizationIntegrationSettings: OrganizationIntegrationSettings;
  onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
}

const IntegrationSettingsRow = ({
  organizationIntegrationSettings,
  onIntegrationToggle,
}: Props) => {
  const { user } = useAppContext();
  const disableOrgScopeEditingFlag = user.is_org_scope_editing_disabled;
  const [showPartnershipcCredentialModal, setShowPartnershipCredentialModal] = useState(false);
  const [orgIntSettings, setOrgIntSettings] = useState(organizationIntegrationSettings);

  // Sync local state when parent prop updates (e.g., after toggle API call)
  useEffect(() => {
    setOrgIntSettings(organizationIntegrationSettings);
  }, [organizationIntegrationSettings]);

  const integrationSettings = useMemo(() => orgIntSettings, [orgIntSettings]);

  const configStatusFields = integrationSettings.organization_config_status;
  const integration = useMemo(
    () => integrationSettings.integration,
    [integrationSettings.integration],
  );

  const syncPlanMap = user.organization.sync_frequency_plans;
  const syncPlanType =
    syncPlanMap?.[integrationSettings.category as string] ||
    syncPlanMap?.[SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_DEFAULT];
  const isSyncFrequencyBillingPlan = (Object as any)
    .values(SyncFrequencyPlan)
    .includes(syncPlanType);

  const MergeBaseURL = process.env.REACT_APP_BASE_API_URL || "https://api.merge.dev";

  const [fieldBeingModified, setFieldBeingModified] =
    useState<OrganizationConfigStatusField | null>(null);
  const [fieldBeingModifiedValue, setFieldBeingModifiedValue] = useState<string | Blob | null>("");
  const [isSavingFieldBeingModified, setIsSavingFieldBeingModified] = useState(false);
  const humanReadableFieldBeingModifiedName = useMemo(
    () => snakeCaseToSpacedCapitalized(fieldBeingModified?.field_name ?? ""),
    [fieldBeingModified],
  );

  const isConfigIncomplete = find(
    configStatusFields,
    (configField: OrganizationConfigStatusField) => {
      return configField.required && !configField.completed;
    },
  );

  const handleFileUpload = useCallback(
    (value: FileList | null) => {
      const certificateFile = value ? value[0] : null;
      if (certificateFile) {
        const blob = new Blob([certificateFile], {
          type: certificateFile.type,
        });
        setFieldBeingModifiedValue(blob);
      }
    },
    [setFieldBeingModifiedValue],
  );

  const handleFieldBeingModifiedSave = useCallback(
    (event: any) => {
      event.preventDefault();
      if (!fieldBeingModified) {
        return;
      }
      let body: {};
      const fieldData: { [field_name: string]: string | Blob | null } = {};
      fieldData[fieldBeingModified.field_name] = fieldBeingModifiedValue;
      if (fieldBeingModified.field_type == "file") {
        body = fieldData;
      } else {
        body = { organization_config_fields: JSON.stringify(fieldData) };
      }

      setIsSavingFieldBeingModified(true);
      updateOrganizationIntegrationSettings({
        settingsId: integrationSettings.id,
        body: body,
        onUpdate: (data: OrganizationIntegrationSettings) => {
          onIntegrationToggle(data);
          setIsSavingFieldBeingModified(false);
          setFieldBeingModified(null);
        },
      });
    },
    [fieldBeingModified, fieldBeingModifiedValue],
  );

  const getCustomerNotesByPartnership = (notes: string, partnership: PartnershipType) => {
    const partnership_placeholder = "{" + partnership + "}";
    return notes.includes(partnership_placeholder)
      ? notes.substring(
          notes.indexOf(partnership_placeholder) + partnership_placeholder.length,
          notes.lastIndexOf(partnership_placeholder),
        )
      : notes;
  };

  let cleanHTMLString = "";
  if (integration.notes_for_customers) {
    cleanHTMLString = getSanitizedHTML(
      getCustomerNotesByPartnership(
        integration.notes_for_customers,
        integrationSettings.partnership_type,
      ),
    );
  }

  const ADP_WORKFORCE_NOW = "198fe785-0a6c-4098-b546-be0a83dd2455";
  const ADP_RUN = "7941cce2-1db2-4281-8473-c6c4741f0609";
  const ADP_NEXT_GEN = "d2ec4855-7613-41cf-a449-c05344961bc8";

  const ADP_PRODUCTS = [ADP_WORKFORCE_NOW, ADP_RUN, ADP_NEXT_GEN];

  const renderADPComponent = (integrationId: string, integrationSettingsId: string) => {
    if (!integrationId || !integrationSettingsId) return null;

    switch (integrationId) {
      case ADP_WORKFORCE_NOW:
      case ADP_RUN:
      case ADP_NEXT_GEN:
        return (
          <div className="flex flex-col gap-y-2">
            <div>
              <Text variant="h6">For customers that have an ADP partnership</Text>
              <Text className="text-gray-40">
                Below is the URL that event notifications from your ADP Marketplace app should be
                sent to.
              </Text>
            </div>
            {/* ADP webhook URL contains a tenant-scoped integration settings ID and is thus masked at the field (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot) */}
            <div className="ph-no-capture" data-fr-ignore>
              <CopyTextField
                text={`${MergeBaseURL}/api/integrations/organization-webhook-listeners/${integrationSettingsId}`}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Section with configuration notes for the integration
  const configurationNotesSection = integration.notes_for_customers && (
    <div className="flex flex-col gap-y-4 px-6 py-4 border-t border-gray-10 overflow-hidden">
      <div>
        <Text variant="h6" className="mb-2">
          Configuration notes
        </Text>
        <span
          className="text-gray-40"
          dangerouslySetInnerHTML={{
            __html: cleanHTMLString,
          }}
        />
      </div>
      {Object.values(ADP_PRODUCTS).includes(integration.id) &&
        renderADPComponent(integration.id, integrationSettings.id)}
      {integration.name === "Rippling" && (
        <div>
          <Text variant="h6" className="mb-2">
            Webhook URL
          </Text>
          {/* Rippling webhook URL contains the tenant organization ID and is thus masked at the field (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot) */}
          <div className="ph-no-capture" data-fr-ignore>
            <CopyTextField
              text={`${MergeBaseURL}/api/integrations/organization-webhook-listener/eogU1dU3JZqCo4hohmIC4lb9resKplMPods3xA1qxRczXwCdhSJeng/${organizationIntegrationSettings.organization.id}`}
            />
          </div>
        </div>
      )}
    </div>
  );

  // If there's a field being edited, this component shows up with ability to edit it
  const editModal = (
    <Dialog
      open={!!fieldBeingModified}
      onClose={() => setFieldBeingModified(null)}
      onPrimaryButtonClick={(e) => handleFieldBeingModifiedSave(e)}
      primaryButtonLoading={isSavingFieldBeingModified}
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      primaryButtonText="Save"
      onSecondaryButtonClick={() => setFieldBeingModified(null)}
      secondaryButtonText="Cancel"
      title={`Set ${integration.name} ${humanReadableFieldBeingModifiedName}`}
      variant="md"
    >
      <p>{fieldBeingModified?.description}</p>
      {fieldBeingModified?.field_type === "string" && (
        <TextField
          onChange={(e) => setFieldBeingModifiedValue(e.currentTarget.value)}
          placeholder={`Enter your ${integration.name} ${humanReadableFieldBeingModifiedName}`}
          variant={TextFieldVariant.Bordered}
        />
      )}
      {fieldBeingModified?.field_type === "file" && (
        // Uploaded credential filenames are tenant-chosen and can carry PII; masked at the file input
        <input
          type="file"
          className="ph-no-capture"
          placeholder={`Upload ${integration.name} ${humanReadableFieldBeingModifiedName}`}
          accept={
            fieldBeingModified?.field_name.toLowerCase() == "SSL certificate file".toLowerCase()
              ? ".pem"
              : ".key,.txt"
          } // TODO: refactor so the files to accept just comes from the backend
          onChange={(e) => handleFileUpload(e.currentTarget.files)}
        />
      )}
    </Dialog>
  );

  const doesNotHaveContent =
    !organizationIntegrationSettings.is_beta &&
    !configurationNotesSection &&
    isSyncFrequencyBillingPlan &&
    !integrationSettings.partnership_available;

  const [isExpanded, setIsExpanded] = useState(false);

  const hasMergeCreds = orgIntSettings.has_merge_creds;
  const usingPartnerCreds =
    orgIntSettings.partnership_type == PartnershipType.ORGANIZATION_PARTNERSHIP;
  return (
    <>
      {editModal}
      <PartnershipCredentialModal
        open={showPartnershipcCredentialModal}
        hasMergeCreds={hasMergeCreds}
        setOrganizationIntegrationSettings={setOrgIntSettings}
        configStatusFields={configStatusFields}
        organizationIntegrationSettingsID={integrationSettings.id}
        onModalClose={() => {
          setShowPartnershipCredentialModal(false);
        }}
        isAlreadyCompleted={!isConfigIncomplete}
        partnershipType={orgIntSettings.partnership_type}
      />
      <Accordion
        title={
          <IntegrationSettingsRowOverview
            settings={integrationSettings}
            onIntegrationToggle={onIntegrationToggle}
            isBeta={organizationIntegrationSettings.is_beta}
            isToggleable={organizationIntegrationSettings.is_toggleable}
          />
        }
        titleClassName={clsx(
          "px-6 py-4 cursor-pointer transition-colors hover:bg-gray-0",
          isExpanded && !doesNotHaveContent ? "rounded-t-lg" : "rounded-lg",
          doesNotHaveContent && "[&_.MuiAccordionSummary-expandIconWrapper]:invisible",
        )}
        chevronOrientation="right"
        expanded={doesNotHaveContent ? false : isExpanded}
        setExpanded={doesNotHaveContent ? undefined : setIsExpanded}
      >
        {/* Beta Alert */}
        {organizationIntegrationSettings.is_beta && (
          <div className="px-4 py-4 border-t border-gray-10">
            <Alert color="blue" showWarningIcon size="sm">
              <Text variant="sm">
                Beta indicates a newer integration that we&apos;ve tested extensively, but has yet
                to be widely adopted. Due to this, edge cases can appear, please let us know if you
                hit any! Once we have sufficient usage, the beta flag will be removed. To request
                access, please reach out to{" "}
                <Link href="mailto:support@merge.dev">support@merge.dev</Link>.
              </Text>
            </Alert>
          </div>
        )}

        {configurationNotesSection}
        {!isSyncFrequencyBillingPlan && (
          <IntegrationSyncFrequencySection
            settings={integrationSettings}
            onIntegrationToggle={onIntegrationToggle}
          />
        )}
        {integrationSettings.partnership_available &&
          organizationIntegrationSettings.is_toggleable && (
            <div className="flex flex-row items-center pl-6 px-5 pb-5 border-t border-gray-10 pt-4">
              {!isConfigIncomplete && usingPartnerCreds ? (
                <span className="mr-2">Currently using my credentials</span>
              ) : (
                hasMergeCreds && <span className="mr-2">Currently using Merge credentials</span>
              )}
              {!disableOrgScopeEditingFlag && (
                <Button
                  variant={ButtonVariant.TextBlue}
                  onClick={() => {
                    setShowPartnershipCredentialModal(true);
                  }}
                  className="p-0"
                >
                  {isConfigIncomplete && !hasMergeCreds
                    ? "Set up organization-level credentials"
                    : "Edit"}
                </Button>
              )}
            </div>
          )}
      </Accordion>
    </>
  );
};

export default IntegrationSettingsRow;
